import * as React from 'react'
import { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateAdminFacility, type AdminFacility } from '@/features/admin/api'
import { Drawer } from '@/components/primitives/Drawer'
import { Button } from '@/components/primitives/Button'
import { Input } from '@/components/primitives/Input'
import { FormField } from '@/components/primitives/FormField'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import { formatPhoneInput, combinePhoneCode, splitPhoneCode } from '@/lib/phone'
import '../ManagePages.css'

interface Props {
  facility: AdminFacility | null
  onClose: () => void
}

export function EditAdminFacilityDrawer({ facility, onClose }: Props): React.JSX.Element {
  const toast = useToast()
  const qc = useQueryClient()

  const [name, setName] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zip, setZip] = useState('')
  const [phone, setPhone] = useState('')
  const [phoneCode, setPhoneCode] = useState('+1')

  useEffect(() => {
    if (facility) {
      setName(facility.name)
      setAddress(facility.address_line1 ?? '')
      setCity(facility.city ?? '')
      setState(facility.state ?? '')
      setZip(facility.postal_code ?? '')
      const parsed = splitPhoneCode(facility.phone)
      setPhone(parsed.phone)
      setPhoneCode(parsed.code)
    }
  }, [facility])

  const update = useMutation({
    mutationFn: () =>
      updateAdminFacility(facility!.id, {
        name: name.trim() || undefined,
        address_line1: address || undefined,
        city: city || undefined,
        state: state || undefined,
        postal_code: zip || undefined,
        phone: combinePhoneCode(phone, phoneCode) || undefined,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-facilities'] })
      toast.success('Facility updated', `${name} has been updated.`)
      onClose()
    },
    onError: (err) =>
      toast.error('Could not update', err instanceof ApiError ? err.message : 'Try again.'),
  })

  return (
    <Drawer
      open={facility !== null}
      onClose={onClose}
      title="Edit facility"
      subtitle={facility ? `Editing ${facility.tenant_name} · ${facility.name}` : ''}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button" disabled={update.isPending}>Cancel</Button>
          <Button onClick={() => update.mutate()} loading={update.isPending} disabled={!name.trim()}>
            Save changes
          </Button>
        </>
      }
    >
      <div className="drawer-sections">
        <div className="drawer-section">
          <div className="drawer-section-label">Facility details</div>
          <FormField label="Facility name" htmlFor="ef-name" required>
            <Input id="ef-name" value={name} onChange={e => setName(e.target.value)} placeholder="Sunrise North" />
          </FormField>
          <div className="drawer-phone-row">
            <FormField label="Code" htmlFor="ef-phone-code">
              <select id="ef-phone-code" className="select" value={phoneCode} onChange={e => setPhoneCode(e.target.value)}>
                <option value="+1">+1</option>
                <option value="+91">+91</option>
                <option value="+41">+41</option>
              </select>
            </FormField>
            <FormField label="Phone" htmlFor="ef-phone">
              <Input
                id="ef-phone"
                type="tel"
                value={phone}
                onChange={e => setPhone(formatPhoneInput(e.target.value))}
                placeholder="(312) 555-0100"
              />
            </FormField>
          </div>
          <FormField label="Address" htmlFor="ef-addr">
            <Input id="ef-addr" value={address} onChange={e => setAddress(e.target.value)} placeholder="123 Main St" />
          </FormField>
          <div className="drawer-3col">
            <FormField label="City" htmlFor="ef-city">
              <Input id="ef-city" value={city} onChange={e => setCity(e.target.value)} placeholder="Chicago" />
            </FormField>
            <FormField label="State" htmlFor="ef-state">
              <Input id="ef-state" value={state} onChange={e => setState(e.target.value)} placeholder="IL" maxLength={2} />
            </FormField>
            <FormField label="ZIP" htmlFor="ef-zip">
              <Input id="ef-zip" value={zip} onChange={e => setZip(e.target.value)} placeholder="60601" />
            </FormField>
          </div>
        </div>

      </div>
    </Drawer>
  )
}
